import React, {useEffect, useRef, useState} from 'react';

// Lines spoken in the room come from the server (server/speech.rb): it says them through Zed or the show engine
// and tells every screen which line is being spoken, from when. Screens only show it.
// The lookup on mount is only for a line already under way: if a live update arrives first (the line that starts
// as the scene appears), the slower lookup's older answer mustn't replace it.
export const useSpeech = (receiveEvents) => {
    const [speech, setSpeech] = useState(null);
    const heardLive = useRef(false);
    useEffect(() => {
        fetch('/speech', {credentials: 'include'}).then(r => r.json())
            .then(d => { if (!heardLive.current) setSpeech(d.speech || null); }).catch(() => {});
    }, []);
    useEffect(() => receiveEvents && receiveEvents((data) => {
        if (data && 'speech' in data) { heardLive.current = true; setSpeech(data.speech || null); }
    }), [receiveEvents]);
    return speech;
};

const SPOKEN_CHARS_PER_SECOND = 17;

// How much of the line has been said by now: Zed's against his own character timings, any other voice at
// speaking pace. Once the line has ended, all of it.
const spokenSoFar = (speech, now) => {
    if (speech.endedAt) return speech.text;
    const t = (now - speech.startedAt) / 1000;
    if (speech.chars && speech.times) {
        let n = 0;
        while (n < speech.times.length && speech.times[n] <= t) n++;
        return speech.chars.slice(0, n).join('');
    }
    return speech.text.slice(0, Math.max(0, Math.floor(t * SPOKEN_CHARS_PER_SECOND)));
};

// speech: the line (from useSpeech), or null for nothing.
export const SpokenLine = ({speech, clockDrift = 0, style = {}}) => {
    const [, tick] = useState(0);
    const shown = speech ? spokenSoFar(speech, Date.now() + clockDrift) : '';
    const done = !speech || speech.endedAt || shown.length >= speech.text.length;
    useEffect(() => {
        if (done) return;
        let id = requestAnimationFrame(function frame() { tick(n => n + 1); id = requestAnimationFrame(frame); });
        return () => cancelAnimationFrame(id);
    }, [done, speech]);
    if (!speech) return null;
    return <span style={{fontSize: 64, backgroundColor: 'black', fontWeight: 'bold', ...style}}>
        <span>{shown}</span>
        {!speech.endedAt && <span> ■</span>}
    </span>;
};
